import { useRef, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { tournamentApi } from "@bw/shared/api";
import { formatDate } from "@bw/shared/utils";
import type { TournamentPlayerTeamEntryResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { useWithdrawTeamEntry } from "../hooks/useWithdrawTeamEntry";
import { TournamentParticipationSummary } from "./TournamentParticipationSummary";

export function TournamentTeamEntries() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const entryId = params.get("team_entry_id") || undefined;
  const rawPage = Number(params.get("team_page"));
  const page =
    !entryId && Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const entries = useQuery({
    queryKey: [
      ...queryKeys.tournaments.myTeamEntries(),
      user?.uid,
      { page, entryId },
    ],
    queryFn: () =>
      tournamentApi.getMyTeamEntries({
        limit: 20,
        offset: (page - 1) * 20,
        ...(entryId ? { team_entry_id: entryId } : {}),
      }),
    enabled: Boolean(user),
  });
  function changePage(value: number) {
    const next = new URLSearchParams(params);
    next.set("team_page", String(value));
    setParams(next);
  }
  if (!user) return null;
  // Nothing to act on: stay quiet rather than a heading over an empty line.
  if (
    !entryId &&
    page === 1 &&
    entries.isSuccess &&
    !entries.data?.items.length
  )
    return null;
  return (
    <Stack
      component="section"
      aria-label="Team entries"
      spacing={1.5}
      sx={{ mb: 3 }}
    >
      <Typography component="h2" variant="h6">
        Team entries
      </Typography>
      {entryId ? (
        <Button
          sx={{ alignSelf: "flex-start" }}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.delete("team_entry_id");
            next.delete("team_page");
            setParams(next);
          }}
        >
          All team entries
        </Button>
      ) : null}
      {entries.isLoading ? (
        <Typography role="status">Loading team entries…</Typography>
      ) : entries.isError ? (
        <Alert
          severity="error"
          action={<Button onClick={() => void entries.refetch()}>Retry</Button>}
        >
          Could not load your team entries.
        </Alert>
      ) : !entries.data?.items.length ? (
        <Typography color="text.secondary">
          {entryId
            ? "This team entry is unavailable."
            : "No team entries on this page."}
        </Typography>
      ) : (
        <List disablePadding component={Paper} variant="outlined">
          {entries.data.items.map((entry) => (
            <TeamRow
              key={`${user.uid}:${entry.entry_id}`}
              entry={entry}
              refreshing={entries.isFetching}
            />
          ))}
        </List>
      )}
      {entries.data &&
      !entries.isError &&
      (page > 1 || entries.data.has_more) ? (
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => changePage(page - 1)}>
            Previous team entries
          </Button>
          <Button
            disabled={!entries.data.has_more}
            onClick={() => changePage(page + 1)}
          >
            Next team entries
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

function TeamRow({
  entry,
  refreshing,
}: {
  entry: TournamentPlayerTeamEntryResponse;
  refreshing: boolean;
}) {
  const withdraw = useWithdrawTeamEntry(entry.tournament_id);
  const [confirming, setConfirming] = useState(false);
  const lock = useRef(false);
  const pending = withdraw.isPending || refreshing;
  async function confirm() {
    if (
      lock.current ||
      pending ||
      !entry.can_withdraw ||
      entry.viewer_role !== "captain" ||
      entry.status !== "active"
    )
      return;
    lock.current = true;
    try {
      await withdraw.mutateAsync(entry.entry_id);
    } catch {
      /* Safe error and refreshed source remain visible. */
    } finally {
      lock.current = false;
      setConfirming(false);
    }
  }
  return (
    <ListItem divider sx={{ display: "block", py: 2 }}>
      <Stack
        spacing={1}
        sx={{ alignItems: "flex-start", overflowWrap: "anywhere" }}
      >
        <Typography sx={{ fontWeight: 700 }}>{entry.team_name}</Typography>
        <Typography>{entry.tournament_name}</Typography>
        <Typography variant="body2">
          {entry.event_name} · {entry.draw_name}
        </Typography>
        <Typography variant="body2">
          {entry.viewer_role === "captain" ? "Captain" : "Member"} · Entered{" "}
          {formatDate(entry.registered_at)}
        </Typography>
        <Chip size="small" label={entry.status} />
        {entry.seed_label ? (
          <Typography variant="body2">Seed: {entry.seed_label}</Typography>
        ) : null}
        <TournamentParticipationSummary
          presence={entry.presence}
          liveUrl={entry.live_url}
          nextMatch={entry.next_match}
        />
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button component={RouterLink} to={entry.detail_url}>
            View team entry
          </Button>
          {entry.results_url ? (
            <Button component={RouterLink} to={entry.results_url}>
              Draw &amp; results
            </Button>
          ) : null}
          {entry.can_withdraw &&
          entry.viewer_role === "captain" &&
          entry.status === "active" ? (
            <Button disabled={pending} onClick={() => setConfirming(true)}>
              Withdraw team
            </Button>
          ) : null}
        </Stack>
        {withdraw.isError ? (
          <Alert severity="error">
            Could not withdraw the team. Review the latest entry status.
          </Alert>
        ) : null}
      </Stack>
      <Dialog
        open={confirming}
        onClose={() => {
          if (!pending) setConfirming(false);
        }}
        aria-labelledby={`withdraw-team-${entry.entry_id}`}
      >
        <DialogTitle id={`withdraw-team-${entry.entry_id}`}>
          Withdraw {entry.team_name}?
        </DialogTitle>
        <DialogContent>
          This withdraws the team’s entry from {entry.draw_name}. Recorded
          results are not erased.
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button
            disabled={pending}
            onClick={() => void confirm()}
            color="error"
          >
            Confirm withdrawal
          </Button>
        </DialogActions>
      </Dialog>
    </ListItem>
  );
}
